import { DishItem } from 'models/entities/dish-item';
import { Ingredient } from 'models/entities/ingredient';
import { IngredientItem } from 'models/entities/ingredient-item';

import { ExportDishItemsWithIngredientsTarget } from '../../../models/entities/target';

type Data = {
  target: ExportDishItemsWithIngredientsTarget;
}

type DataToExport = {
  dishItemId: string;
  menuCategoryId: string;
  dishCategoryId: string;
  dishOrder: string;
  dishName: string;
  dishPrice: string;
  dishPosItemId: string;
  dishIngredientItemId: string;
  dishCost: string;
  dishCostRate: string;
  dishCreatedAt: string;
  dishUpdatedAt: string;
  ingredientId: string;
  ingredientOrder: string;
  ingredientUsage: string;
  ingredientCost: string;
  ingredientCreatedAt: string;
  ingredientUpdatedAt: string;
  ingredientItemId: string;
  ingredientItemType: string;
  ingredientCategoryId: string;
  ingredientName: string;
  ingredientSupplierId: string;
  ingredientSupplierName: string;
  ingredientPoundPrice: string;
  ingredientUnitValue: string;
  ingredientUnitSymbol: string;
  ingredientNetPrice: string;
  ingredientGrossPrice: string;
  ingredientUnitPrice: string;
  ingredientYieldRate: string;
};

const fields = ['dishItemId', 'menuCategoryId', 'dishCategoryId', 'dishOrder', 'dishName', 'dishPrice', 'dishPosItemId', 'dishIngredientItemId', 'dishCost', 'dishCostRate', 'dishCreatedAt', 'dishUpdatedAt', 'ingredientId', 'ingredientOrder', 'ingredientUsage', 'ingredientCost', 'ingredientCreatedAt', 'ingredientUpdatedAt', 'ingredientItemId', 'ingredientItemType', 'ingredientCategoryId', 'ingredientName', 'ingredientSupplierId', 'ingredientSupplierName', 'ingredientPoundPrice', 'ingredientUnitValue', 'ingredientUnitSymbol', 'ingredientNetPrice', 'ingredientGrossPrice', 'ingredientUnitPrice', 'ingredientYieldRate'];

class Model {

  readonly target: ExportDishItemsWithIngredientsTarget;
  readonly tsv: string;
  readonly loading: boolean;

  constructor(data: Data) {
    this.target = data.target;
    this.tsv = '';
    this.loading = true;
  }

  async load(): Promise<this> {
    const dishItems = await Promise.all(this.target.dishItems.map(it => this.loadDishItem(it)));
    const tsv = this.export(dishItems);
    return Object.assign(Object.create(this.constructor.prototype), { ...this, tsv, loading: false });
  }

  private async loadDishItem(item: DishItem): Promise<DishItem> {
    if (!item.ingredients.nextToken) return item;
    const ingredients = await item.ingredients.readAll();
    return item.apply({ ingredients });
  }

  private export(items: DishItem[]): string {
    const data = items.flatMap(it => this.convertRows(it)).map(it => Object.values(it).join('\t'));
    return [fields.join('\t'), ...data].join('\n');
  }

  private convertRows(item: DishItem): DataToExport[] {
    if (!item.ingredients.documents.length) return [this.convert(item)];
    return item.ingredients.documents.map(ingredient => this.convert(item, ingredient));
  }

  private convert(item: DishItem, ingredient?: Ingredient): DataToExport {
    const ingredientItem = ingredient?.item;
    const dishItemId = item.id;
    const menuCategoryId = item.menuCategoryId;
    const dishCategoryId = item.category.id;
    const dishOrder = item.order.toString();
    const dishName = item.name;
    const dishPrice = item.price.toString();
    const dishPosItemId = item.posItemId;
    const dishIngredientItemId = item.ingredientItemId;
    const dishCost = item.cost.toString();
    const dishCostRate = item.costRate.toString();
    const dishCreatedAt = item.createdAt?.toJSON() ?? '';
    const dishUpdatedAt = item.updatedAt?.toJSON() ?? '';
    const ingredientId = ingredient?.id ?? '';
    const ingredientOrder = ingredient?.order.toString() ?? '';
    const ingredientUsage = ingredient?.usage.toString() ?? '';
    const ingredientCost = ingredient?.cost?.toString() ?? '';
    const ingredientCreatedAt = ingredient?.createdAt?.toJSON() ?? '';
    const ingredientUpdatedAt = ingredient?.updatedAt?.toJSON() ?? '';
    const ingredientItemId = ingredientItem?.id ?? '';
    const ingredientItemType = this.getIngredientItemType(ingredientItem);
    const ingredientCategoryId = ingredientItem?.category.id ?? '';
    const ingredientName = ingredientItem?.name ?? '';
    const ingredientSupplierId = ingredientItem?.supplier?.id ?? '';
    const ingredientSupplierName = ingredientItem?.supplier?.name ?? '';
    const ingredientPoundPrice = ingredientItem?.poundPrice?.toString() ?? '';
    const ingredientUnitValue = ingredientItem?.unitValue?.toString() ?? '';
    const ingredientUnitSymbol = ingredientItem?.unitSymbol ?? '';
    const ingredientNetPrice = ingredientItem?.netPrice?.toString() ?? '';
    const ingredientGrossPrice = ingredientItem?.grossPrice?.toString() ?? '';
    const ingredientUnitPrice = ingredientItem?.unitPrice?.toString() ?? '';
    const ingredientYieldRate = ingredientItem?.yieldRate?.toString() ?? '';
    return {
      dishItemId,
      menuCategoryId,
      dishCategoryId,
      dishOrder,
      dishName,
      dishPrice,
      dishPosItemId,
      dishIngredientItemId,
      dishCost,
      dishCostRate,
      dishCreatedAt,
      dishUpdatedAt,
      ingredientId,
      ingredientOrder,
      ingredientUsage,
      ingredientCost,
      ingredientCreatedAt,
      ingredientUpdatedAt,
      ingredientItemId,
      ingredientItemType,
      ingredientCategoryId,
      ingredientName,
      ingredientSupplierId,
      ingredientSupplierName,
      ingredientPoundPrice,
      ingredientUnitValue,
      ingredientUnitSymbol,
      ingredientNetPrice,
      ingredientGrossPrice,
      ingredientUnitPrice,
      ingredientYieldRate,
    };
  }

  private getIngredientItemType(item?: IngredientItem): string {
    if (!item) return '';
    if (item.dishItem) return 'in-house';
    if (item.poundPrice !== undefined) return 'purchased-pound';
    return 'purchased-unit';
  }

}

export { Model };
